import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';
import { isImportFile } from '../../../lib/utils/csvParser';

const FileUploadZone = ({ onFileSelect, selectedFile, disabled = false, maxCandidates = 100, currentCount = 0, remainingSlots = 100 }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      const file = files?.[0];
      if (isImportFile(file)) {
        onFileSelect(file);
      } else {
        alert(t('import.upload.invalidFileType'));
      }
    }
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      const file = files[0];
      if (isImportFile(file)) {
        onFileSelect(file);
      } else {
        alert(t('import.upload.invalidFileType'));
        e.target.value = '';
      }
    }
  };

  const handleBrowseClick = () => {
    if (disabled) return;
    fileInputRef?.current?.click();
  };

  const handleRemoveFile = () => {
    onFileSelect(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-xl font-semibold text-foreground mb-4">{t('import.upload.title')}</h2>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
          isDragging && !disabled && "border-primary bg-primary/5",
          !isDragging && "border-border",
          disabled && "opacity-50 cursor-not-allowed",
          !disabled && "cursor-pointer hover:border-primary/50"
        )}
        onClick={!disabled && !selectedFile ? handleBrowseClick : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {!selectedFile ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Upload" size={32} className="text-primary" />
              </div>
            </div>
            
            <div>
              <p className="text-lg font-medium text-foreground mb-1">
                {t('import.upload.dragDrop')}
              </p>
              <p className="text-sm text-muted-foreground mb-3">
                {t('import.upload.orBrowse')}
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleBrowseClick();
                }}
                disabled={disabled}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="FolderOpen" size={16} />
                {t('import.upload.browse')}
              </button>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>• {t('import.upload.acceptedFormats')}</p>
              <p>• {t('import.upload.maxFileSize')}</p>
              <p>• {t('import.upload.slotsPerUpload', { remaining: remainingSlots, current: currentCount, max: maxCandidates })}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded bg-success/10 flex items-center justify-center">
                <Icon name="FileText" size={20} className="text-success" />
              </div>
              <div className="text-left">
                <p className="font-medium text-foreground">{selectedFile?.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(selectedFile?.size / 1024)?.toFixed(2)} KB
                </p>
              </div>
            </div>
            {!disabled && (
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  handleRemoveFile();
                }}
                className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                title={t('import.upload.removeFile')}
              >
                <Icon name="X" size={20} className="text-destructive" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadZone;
